import { cn } from '@/lib/utils';
import {
  type LucideIcon,
  Users,
  ChevronDown,
  ChevronRight,
  FilePlus,
  Car,
  LayoutGrid,
  CalendarDays,
  HandCoins,
  FilePieChart,
  Receipt,
  BadgeDollarSign,
  TicketPercent,
  Shield,
  Gamepad2,
  BookUser,
  Umbrella,
  ImagePlus,
  TreePine,
  NotebookTabs,
  SquareUser,
  Link,
  FileUp,
  User2,
  Languages,
  ChevronUp,
  LogOut,
  Banknote,
  Settings,
  Bell,
  Sun,
  Moon,
} from 'lucide-react';
import { useState, useRef, type MouseEvent } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { useAppStore, type LocaleCode } from '@/modules/shared/store/appStore';
import { en as commonEn, th as commonTh } from '@/modules/shared/i18n/locales';
import { useTheme } from '@/providers/theme-provider';
import { en as navEn, th as navTh } from './locales';
import { trackEvent } from '@/modules/shared/analytics';
import { Sidebar, SidebarSection, SidebarItem, SidebarFooter, useSidebar } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { performLogout } from '@/modules/auth/shared/performLogout';
import NotificationSidebarItem from '@/modules/shared/notifications/NotificationSidebarItem';
import GlobalSearch from '@/modules/global-search';
import { testIds } from '@/test/testIds';

const routeToTestIdSegment = (to: string): string =>
  to
    .replace(/^\//, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

type Language = 'english' | 'thai';

interface NavChild {
  label: string;
  to: string;
}

interface NavItemData {
  label: string;
  icon?: LucideIcon;
  to?: string;
  dropdown?: boolean;
  children?: NavChild[];
  type?: 'notifications';
  /** Stable kebab-case identifier for dropdown groups (labels are translated, so
   *  they can't be used as testid segments). Only needed for `dropdown: true` items. */
  key?: string;
}

/** Minimal shapes of the appStore slices this component reads. */
interface NavUser {
  id?: number;
  fullname?: string;
  roles?: string[];
}

const MainNavigation = () => {
  const user = useAppStore((s) => s.user) as NavUser | null;
  const language = useAppStore((s) => s.language);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const { theme, setTheme } = useTheme();

  const { state, setOpen, toggleSidebar, isMobile } = useSidebar();
  const isCollapsed = !isMobile && state === 'collapsed';
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation translations: shared common + nav-specific keys (nav wins on collision)
  const { t } = useTranslation({ en: { ...commonEn, ...navEn }, th: { ...commonTh, ...navTh } });

  // Language state lives in the Zustand appStore (LocaleCode 'en' | 'th').
  const handleLanguageChange = (lang: Language) => {
    const localeCode: LocaleCode = lang === 'english' ? 'en' : 'th';
    useAppStore.getState().setLanguage(localeCode);
    localStorage.setItem('language', lang);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // Helper function to check if a link is active.
  const isLinkActive = (linkTo: string, { isDropdownChild }: { isDropdownChild?: boolean } = {}) => {
    const [linkPath, linkQuery] = linkTo.split('?');
    const currentPath = location.pathname;
    const currentQuery = location.search.slice(1); // Remove the '?' prefix

    // If link has query params, require EXACT path AND exact query match
    if (linkQuery) {
      return currentPath === linkPath && currentQuery === linkQuery;
    }

    // Top-level item (no dropdown): exact path match only
    if (!isDropdownChild) {
      return currentPath === linkPath;
    }

    // Dropdown child: exact or prefix match (e.g. /leads active on /leads and /leads/new-leads)
    return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
  };

  const trackNavClick = (name: string) => {
    trackEvent('nav_submenu_clicked', { name });
  };

  // Sidebar options array with translations
  const sidebarOptions: NavItemData[] = [
    {
      label: t('createLeads'),
      icon: FilePlus,
      to: '/create-leads',
    },
    {
      label: t('motorPolicy'),
      icon: Car,
      dropdown: true,
      children: [
        { label: t('voluntaryInsurance'), to: '/voluntary-insurance' },
        // { label: t('leads'), to: '/leads' },
        // { label: t('policyInProgress'), to: '/policy-in-progress' },
        { label: t('compulsoryInsurance'), to: '/compulsory-insurance' },
        { label: t('endorsement'), to: '/endorsement' },
      ],
    },
    {
      label: t('nonMotorPolicy'),
      icon: LayoutGrid,
      dropdown: true,
      children: [
        { label: t('nonStandardLeads'), to: '/non-standard-leads' },
        { label: t('nonStandardPolicies'), to: '/non-standard-in-progress' },
        { label: t('nonMotor'), to: '/non-motor-leads' },
      ],
    },
    {
      label: t('agents'),
      icon: Users,
      dropdown: true,
      children: [
        { label: t('allAgents'), to: '/agents/all-agents' },
        { label: t('mlmAgents'), to: '/agents/mlm-agents' },
        { label: t('cmiSubagents'), to: '/agents/cmi-subagents' },
      ],
    },
    { label: t('eventCalendar'), icon: CalendarDays, to: '/event-calendar' },
    { label: t('debtCollectionCRM'), icon: HandCoins, to: '/crm/debt-collection' },
    {
      label: t('accounting'),
      icon: Receipt,
      dropdown: true,
      children: [
        { label: t('agents'), to: '/accounting/agents' },
        { label: t('insurerTables'), to: '/accounting/insurer-tables' },
        { label: t('instalments'), to: '/accounting/instalments' },
        { label: t('credits'), to: '/accounting/credits' },
        { label: t('uploadWHTCertificate'), to: '/accounting/upload-wht' },
      ],
    },
    { label: t('priceListAdmin'), icon: BadgeDollarSign, to: '/price-list-admin' },
    { label: t('gamificationAdmin'), icon: Gamepad2, to: '/gamification-admin?type=bulk_redemption' },
    {
      label: t('promotionAdmin'),
      icon: TicketPercent,
      dropdown: true,
      children: [
        { label: t('packagePromotion'), to: '/promotion-admin/package-promotion' },
        { label: t('campaignPromotion'), to: '/promotion-admin/campaign-promotion' },
      ],
    },
    {
      label: t('policyAdmin'),
      icon: FileUp,
      dropdown: true,
      children: [
        { label: t('policyTrackingIds'), to: '/policy-admin?type=policy_tracking_id' },
        { label: t('cancellationTrackingIds'), to: '/policy-admin?type=cancellation_tracking_id' },
      ],
    },
    {
      label: t('staffManagement'),
      icon: BookUser,
      dropdown: true,
      children: [
        { label: t('manageStaff'), to: '/manage-staff?type=staff' },
        { label: t('staffTiming'), to: '/manage-staff?type=staff_timings&page=1' },
        { label: t('manageLeave'), to: '/manage-staff?type=leaves&page=1' },
        { label: t('uploadSupportStaffCSV'), to: '/manage-staff?type=bulk_support_staff&page=1' },
        { label: t('teamList'), to: '/manage-staff?type=team_list' },
      ],
    },
    {
      label: t('insurerSetting'),
      icon: Umbrella,
      dropdown: true,
      children: [
        { label: t('insurerEmail'), to: '/insurer-settings/insurer-email' },
        { label: t('insurerToggles'), to: '/insurer-settings/insurer-toggles' },
        { label: t('nonMotorInsurerEmail'), to: '/insurer-settings/non-motor-insurer-email' },
        { label: t('refundRules'), to: '/insurer-settings/refund-rules' },
      ],
    },
    {
      label: t('irSettings'),
      icon: Shield,
      dropdown: true,
      children: [
        { label: t('preQuoteSLA'), to: '/ir-settings/pre-quote-sla' },
        { label: t('preSaleSLA'), to: '/ir-settings/pre-sale-sla' },
        { label: t('policyIssuanceSLA'), to: '/ir-settings/policy-issuance-sla' },
        { label: t('coaForm'), to: '/ir-settings/coa-form' },
        { label: t('insurer'), to: '/ir-settings/insurer' },
        { label: t('insurerTier'), to: '/ir-settings/insurer-tier' },
        { label: t('affiliateSpecificCommissionRates'), to: '/ir-settings/affiliate-specific-commission-rates' },
        { label: t('insurerRanking'), to: '/ir-settings/insurer-ranking' },
      ],
    },
    {
      label: t('commissionPromotions'),
      icon: HandCoins,
      dropdown: true,
      children: [{ label: t('agentTransfers'), to: '/commission-and-promotions/agent-transfers' }],
    },
    {
      label: t('bannerUpload'),
      icon: ImagePlus,
      dropdown: true,
      children: [
        { label: t('highlightUpload'), to: '/banner-upload?type=highlightUpload' },
        { label: t('announcementUpload'), to: '/banner-upload?type=announcementUpload' },
        { label: t('commissionHighlight'), to: '/banner-upload?type=commissionHighlight' },
        { label: t('promotionHighlight'), to: '/banner-upload?type=promotionHighlight' },
        { label: t('announcementInventory'), to: '/banner-upload?type=announcementInventory' },
      ],
    },
    { label: t('holidays'), icon: TreePine, to: '/holidays' },
    { label: t('crm'), icon: NotebookTabs, to: '/crm' },
    { label: t('staffForTask'), icon: SquareUser, to: '/staff-for-tasks' },
    { label: t('generateShortenedURL'), icon: Link, to: '/shortened-url' },
    { label: t('historicalReportRequests'), icon: FilePieChart, to: '/historical-report-requests' },
    { label: t('chatwootPayments'), icon: Banknote, to: '/chatwoot-payments' },
    { label: t('assignmentConfig'), icon: Settings, to: '/assignment-config' },
    // Notifications: special-cased below as a popover trigger (see render path).
    { label: t('notificationsAdmin'), icon: Bell, type: 'notifications' },
  ];

  return (
    <Sidebar
      className="bg-sidebar overflow-hidden border-r flex flex-col h-full justify-between"
      side="left"
      collapsible="icon"
      variant="sidebar"
    >
      {/* Header */}
      {/* Collapsed rail is 48px (--sidebar-width-icon): shrink the logo to 32px and
          center it so it keeps 8px padding from the sidebar edges (FTT-15409). */}
      <div className={cn('flex flex-col gap-2', isCollapsed ? 'px-2 py-4 items-center' : 'p-4')}>
        <div className="flex items-center gap-3">
          <NavLink
            to="/"
            className="flex items-center gap-3 transition-opacity text-sidebar-foreground hover:text-foreground"
          >
            <img
              src="/fairdee-logo.svg"
              alt="Logo"
              className={cn('flex-shrink-0', isCollapsed ? 'w-8 h-8' : 'w-10 h-10')}
            />
            {!isCollapsed && (
              <div>
                <div className="text-sm font-medium">{t('adminDashboard')}</div>
                <div className="text-xs text-muted-foreground">v2.0</div>
              </div>
            )}
          </NavLink>
        </div>
        <div className="mt-3">
          <GlobalSearch />
        </div>
      </div>
      {/* Scrollable Section */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <SidebarSection>
          {sidebarOptions.map((item) => {
            if (item.type === 'notifications') {
              return <NotificationSidebarItem key={item.label} label={item.label} isCollapsed={isCollapsed} />;
            }
            if (item.dropdown && item.children) {
              // The collapsed rail hides open groups; expanding shows them again.
              const isOpen = !isCollapsed && (openDropdowns[item.label] || false);
              return (
                <SidebarItem
                  key={item.label}
                  icon={item.icon}
                  label={
                    <div className="flex items-center justify-between w-full">
                      {!isCollapsed && <span>{item.label}</span>}
                      {!isCollapsed &&
                        (isOpen ? (
                          <ChevronDown className="ml-2 h-4 w-4 transition-transform duration-300" />
                        ) : (
                          <ChevronRight className="ml-2 h-4 w-4 transition-transform duration-300" />
                        ))}
                    </div>
                  }
                  isCollapsed={isCollapsed}
                  collapsible
                  open={isOpen}
                  onClick={(e: MouseEvent) => {
                    // Expand the rail so the group's children show. Never on the sheet, where
                    // `toggleSidebar` closes the sheet itself.
                    if (isCollapsed && !isMobile) {
                      toggleSidebar();
                    }
                    setOpenDropdowns((prev) => ({ ...prev, [item.label]: !isOpen }));
                    e.stopPropagation();
                  }}
                  data-testid={testIds.nav.sidebar.dropdownToggle(item.key ?? 'unknown')}
                >
                  <div className="border-l border-border mx-5 flex flex-col space-y-1 bg-transparent">
                    {item.children.map((child) => {
                      const childActive = isLinkActive(child.to, { isDropdownChild: true });
                      return (
                        <NavLink
                          key={child.label}
                          to={child.to}
                          className={() =>
                            cn(
                              'flex items-center space-x-3 px-2.5 py-1 rounded-[8px] mx-2 my-1 transition-all duration-200 font-prompt hover:text-foreground hover:bg-accent text-sm',
                              childActive
                                ? 'text-foreground bg-accent'
                                : 'text-foreground/75 dark:text-muted-foreground',
                              isCollapsed && 'justify-center px-1 mx-1',
                            )
                          }
                          data-testid={testIds.nav.sidebar.link(routeToTestIdSegment(child.to))}
                          onClick={(e) => {
                            if (childActive) {
                              e.preventDefault();
                              return;
                            }
                            e.preventDefault();
                            navigate(child.to);
                            trackNavClick(child.label);
                          }}
                        >
                          {child.label}
                        </NavLink>
                      );
                    })}
                  </div>
                </SidebarItem>
              );
            }
            return (
              <NavItem
                key={item.label}
                to={item.to}
                icon={item.icon}
                label={item.label}
                isCollapsed={isCollapsed}
                navigate={navigate}
                isLinkActive={isLinkActive}
                trackNavClick={trackNavClick}
              />
            );
          })}
        </SidebarSection>
      </div>

      <SidebarFooter className="bg-muted border-t border-border">
        <div className="flex flex-col items-center gap-1">
          {/* User Profile Card */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div
                className={cn(
                  'flex items-center w-full cursor-pointer group py-2 px-2',
                  isCollapsed && 'justify-center',
                  isCollapsed && 'px-0',
                )}
              >
                <div className="bg-foreground rounded-xl w-8 h-8 flex items-center justify-center">
                  <User2 className="w-5 h-5 text-background" />
                </div>
                {!isCollapsed && (
                  <div className="flex flex-col justify-center ml-6">
                    <div className="text-sm font-medium text-foreground group-hover:text-foreground transition-colors">
                      {user?.fullname || '[User Name]'}
                    </div>
                    <div className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                      {user?.roles?.[0] || '[Role]'}
                    </div>
                  </div>
                )}
                {!isCollapsed && (
                  <div className="flex flex-col items-center ml-auto">
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    <ChevronDown className="w-4 h-4 text-muted-foreground -mt-2" />
                  </div>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="center"
              className={cn(
                'z-[100] bg-popover rounded-xl',
                // Collapsed: the trigger is icon-width, so anchor-width squishes the
                // menu into a narrow strip — use a fixed width instead.
                isCollapsed ? 'w-56' : 'min-w-[var(--radix-popper-anchor-width)] w-[var(--radix-popper-anchor-width)]',
              )}
            >
              <div className="flex items-center gap-3 px-4 pt-3 pb-2 border-b border-border">
                <div className="bg-foreground rounded-xl w-8 h-8 flex items-center justify-center">
                  <User2 className="w-5 h-5 text-background" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">{user?.fullname || '[User Name]'}</span>
                  <span className="text-xs text-muted-foreground">{user?.fullname || '[User Name]'}</span>
                </div>
              </div>
              <DropdownMenuItem
                className="border-b border-border py-3 cursor-pointer"
                onClick={() => navigate('/profile')}
                data-testid={testIds.nav.header.profileMenu}
              >
                <User2 className="w-4 h-4 mr-2 text-muted-foreground" />
                <span>{t('profile')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive py-3 cursor-pointer"
                onClick={() => {
                  void performLogout();
                }}
                data-testid={testIds.nav.header.logout}
              >
                <LogOut className="w-4 h-4 mr-2 text-destructive" />
                <span>Log Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div
                className={cn(
                  'flex items-center w-full cursor-pointer group py-2 px-2',
                  isCollapsed && 'justify-center',
                  isCollapsed && 'px-0',
                )}
                data-testid={testIds.nav.header.profileTrigger}
              >
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-foreground mr-1" />
                ) : (
                  <Sun className="w-5 h-5 text-foreground mr-1" />
                )}
                {!isCollapsed && (
                  <span className="text-sm font-medium text-foreground flex-1">
                    {theme === 'dark' ? t('themeDark') : t('themeLight')}
                  </span>
                )}
                {!isCollapsed && <ChevronDown className="w-5 h-5 ml-auto text-muted-foreground" />}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="center"
              className={cn(
                'z-[100] bg-popover rounded-xl',
                // Collapsed: the trigger is icon-width, so anchor-width squishes the
                // menu into a narrow strip — use a fixed width instead.
                isCollapsed ? 'w-56' : 'min-w-[var(--radix-popper-anchor-width)] w-[var(--radix-popper-anchor-width)]',
              )}
            >
              <DropdownMenuItem onClick={() => setTheme('light')} className="py-1 cursor-pointer gap-2">
                <span
                  className={cn(
                    'text-xl text-foreground w-4 inline-block',
                    theme === 'light' ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  &bull;
                </span>
                <span>{t('themeLight')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')} className="py-1 cursor-pointer gap-2">
                <span
                  className={cn(
                    'text-xl text-foreground w-4 inline-block',
                    theme === 'dark' ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  &bull;
                </span>
                <span>{t('themeDark')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div
                className={cn(
                  'flex items-center w-full cursor-pointer group py-2 px-2',
                  isCollapsed && 'justify-center',
                  isCollapsed && 'px-0',
                )}
                data-testid={testIds.nav.header.languageTrigger}
              >
                <Languages className="w-5 h-5 text-foreground mr-1" />
                {!isCollapsed && (
                  <span className="text-sm font-medium text-foreground flex-1">
                    {language === 'th' ? 'ไทย' : 'English'}
                  </span>
                )}
                {!isCollapsed && <ChevronDown className="w-5 h-5 ml-auto text-muted-foreground" />}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="center"
              className={cn(
                'z-[100] bg-popover rounded-xl',
                // Collapsed: the trigger is icon-width, so anchor-width squishes the
                // menu into a narrow strip — use a fixed width instead.
                isCollapsed ? 'w-56' : 'min-w-[var(--radix-popper-anchor-width)] w-[var(--radix-popper-anchor-width)]',
              )}
            >
              <DropdownMenuItem
                onClick={() => handleLanguageChange('english')}
                className="py-1 cursor-pointer gap-2"
                data-testid={testIds.nav.header.languageOption('en')}
              >
                <span
                  className={cn(
                    'text-xl text-foreground w-4 inline-block',
                    language === 'en' ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  &bull;
                </span>
                <span>English</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleLanguageChange('thai')}
                className="py-1 cursor-pointer gap-2"
                data-testid={testIds.nav.header.languageOption('th')}
              >
                <span
                  className={cn(
                    'text-xl text-foreground w-4 inline-block',
                    language === 'th' ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  &bull;
                </span>
                <span>ไทย</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

interface NavItemProps {
  to?: string;
  icon?: LucideIcon;
  label: string;
  onClick?: () => void;
  isCollapsed: boolean;
  navigate: (to: string) => void;
  isLinkActive: (to: string, opts?: { isDropdownChild?: boolean }) => boolean;
  trackNavClick: (name: string) => void;
}

const NavItem = ({
  to,
  icon: Icon,
  label,
  onClick,
  isCollapsed,
  navigate,
  isLinkActive,
  trackNavClick,
}: NavItemProps) => {
  const active = to ? isLinkActive(to) : false;
  return (
    <NavLink
      to={to ?? '#'}
      className={() =>
        cn(
          'flex items-center space-x-3 px-2.5 py-2 rounded-[8px] mx-2 my-1 transition-all duration-200 font-prompt hover:text-foreground hover:bg-accent',
          active ? 'text-foreground bg-accent' : 'text-foreground/75 dark:text-muted-foreground',
          isCollapsed && 'justify-center px-1 mx-1',
        )
      }
      data-testid={testIds.nav.sidebar.link(routeToTestIdSegment(to ?? ''))}
      onClick={(e) => {
        if (active) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        if (to) navigate(to);
        onClick && onClick();
        trackNavClick(label);
      }}
    >
      {Icon && (
        <span className={cn('flex-shrink-0')}>
          <Icon className="h-5 w-5" />
        </span>
      )}
      {!isCollapsed && <span className="text-sm font-medium">{label}</span>}
    </NavLink>
  );
};

export default MainNavigation;
